import classNames from 'classnames';
import type { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import BellFilled from '@alltrails/denali/icons/BellFilled';
import LockOpenFilled from '@alltrails/denali/icons/LockOpenFilled';
import VerifiedFilled from '@alltrails/denali/icons/VerifiedFilled';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import styles from './TrialReminder.module.scss';

type Props = {
  currencyCode: CurrencyCode;
  cost: number;
  tier: PurchaseTier;
  heading?: ReactNode;
  // 'card' (default): bordered card with a heading. 'inline': timeline only, for embedding in a selector card.
  variant?: 'card' | 'inline';
  // Only used by the 'inline' variant to drive the expand/collapse animation.
  selected?: boolean;
};

const bold = (chunks: ReactNode) => (
  <Typography component="span" variant="text200bold">
    {chunks}
  </Typography>
);

const TrialReminderRow = ({ icon, message, isLast }: { icon: ReactNode; message: ReactNode; isLast: boolean }) => (
  <div className={styles.row}>
    <div aria-hidden className={styles.iconCell}>
      {icon}
      {!isLast && <span className={styles.connector} />}
    </div>
    <Typography className={styles.text} component="span" variant="text200">
      {message}
    </Typography>
  </div>
);

// Net-new upsell copy of the payment TrialReminder, rendered as the design's "Trial Module" (bordered card + colored timeline).
// Design: https://www.figma.com/design/5lZfiEveGScwx75JKpB12W/Upsell-XPs-2026?node-id=4723-14734&m=dev
const TrialReminder = ({ currencyCode, cost, tier, heading, variant = 'card', selected = true }: Props) => {
  const iconColor = tier === 'peak' ? '--color-text-peak' : '--color-text-plus';
  const annualCost = <FormattedNumber currency={currencyCode} style="currency" value={cost} />;

  const steps: { icon: ReactNode; message: ReactNode }[] = [
    {
      icon: <LockOpenFilled color={iconColor} size="xs" />,
      message: <FormattedMessage defaultMessage="<b>Today:</b> Start your free trial" values={{ b: bold }} />
    },
    {
      icon: <BellFilled color={iconColor} size="xs" />,
      message: <FormattedMessage defaultMessage="<b>Day {reminderDay}:</b> Get a trial reminder" values={{ b: bold, reminderDay: '5' }} />
    },
    {
      icon: <VerifiedFilled color={iconColor} size="xs" />,
      message: (
        <FormattedMessage
          defaultMessage="<b>Day {trialEndDay}:</b> You’ll be charged <b>{annualCost}</b>/year"
          values={{ annualCost, b: bold, trialEndDay: '7' }}
        />
      )
    }
  ];

  const timeline = (
    <div className={styles.timeline}>
      {steps.map((step, index) => (
        <TrialReminderRow key={index} icon={step.icon} isLast={index === steps.length - 1} message={step.message} />
      ))}
    </div>
  );

  if (variant === 'inline') {
    return (
      <div className={classNames(styles.inline, { [styles.selected]: selected })}>
        <div className={styles.inlineInner}>{timeline}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Typography color="tertiary" variant="text100bold">
        {heading ?? <FormattedMessage defaultMessage="How your free trial works" />}
      </Typography>
      {timeline}
    </div>
  );
};

export default TrialReminder;
